import * as React from "react"
import { NetworkStatus } from "@apollo/client"
import {
  CategoryType,
  type DirectionFromOrigin,
  type EntityCategory,
  type IntervalOrigin,
  type ListTransactionsQuery,
  type ListTransactionsQueryVariables,
  type SummarizeTransactionsByCategoryByTimeQuery,
  useListTransactionsQuery,
  useSummarizeTransactionsByCategoryByTimeQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { LogoLoading } from "@digits-shared/components/Loaders"
import { type DigitsRoute } from "@digits-shared/components/Router/DigitsRoute"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import { hasFirstElement } from "@digits-shared/helpers/arrayHelper"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import { defined } from "@digits-shared/helpers/filters"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import styled from "styled-components"
import { useAnalysisCategoryTypes } from "src/frontend/components/OS/Applications/Analysis/AnalysisCategoryTypesContext"
import { ExportTransactionsType } from "src/frontend/components/OS/Applications/Shared/Accounting/Exports/hooks/useExportVariables"
import { BrowserFiltersBar } from "src/frontend/components/OS/Applications/Shared/Transactions/Browser/Filters/BrowserFiltersBar"
import {
  TransactionListContext,
  TransactionListDisplayOptionsSource,
  useBuildTransactionListContext,
} from "src/frontend/components/OS/Applications/Shared/Transactions/TransactionListContext"
import { INITIAL_TRANSACTION_LIST_PAGINATION } from "src/frontend/components/OS/Applications/Shared/Transactions/transactionListRequestVariables"
import { TransactionSort } from "src/frontend/components/OS/Applications/Shared/Transactions/TransactionSort"
import {
  TransactionsDisplayOptionsContext,
  useBuildTransactionsDisplayOptionsContext,
  useTransactionsDisplayOptions,
} from "src/frontend/components/OS/Shared/DisplayOptions/TransactionsDisplayOptionsContext"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { type ExportOptions } from "src/frontend/components/Shared/Transactions/Categories/categoryTableTypes"
import { CategoryTransactionsTable } from "src/frontend/components/Shared/Transactions/Categories/CategoryTransactionsTable"
import { WithTransactionsUpdates } from "src/frontend/components/Shared/Transactions/Updates/WithTransactionsUpdates"
import { Paginator, type PaginatorHasMore } from "src/shared/components/Paginator"
import {
  convertToDimensionSummaryTree,
  type HierarchicalDimensionSummaryNode,
} from "src/shared/helpers/hierarchicalDimensionHelper"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
 STYLES
*/

const StyledTransactionsTable = styled(CategoryTransactionsTable)`
  tbody {
    tr:last-child {
      border-bottom-color: transparent;
      box-shadow: none;
    }
  }
`

const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 0;
`

const NoMoreData = styled.div`
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`

/*
 INTERFACES
*/

interface Props {
  baseRoute?: DigitsRoute
  title: string
}

/*
 COMPONENTS
*/

export const TransactionBrowser: React.FC<Props> = ({ baseRoute, title }) => {
  const { filterTerm } = useTransactionsDisplayOptions()
  const displayOptions = useBuildTransactionsDisplayOptionsContext(true)
  const transactionFilters = useBuildTransactionListContext({
    displayOptionsSource: TransactionListDisplayOptionsSource.Mixed,
  })
  const { routeToNewDisplayOptions } = transactionFilters
  const onSortingChange = React.useCallback<OnChangeFn<SortingState>>(
    (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(transactionFilters.displayOptions.sort.toTableSort)
          : updaterOrValue

      routeToNewDisplayOptions({
        partialState: { sort: TransactionSort.fromTableSort(newSorting[0]) },
      })
    },
    [routeToNewDisplayOptions, transactionFilters.displayOptions.sort.toTableSort]
  )

  const variables = { ...transactionFilters.requestVariables }
  const { intervalOrigin, paginationDirection } = variables

  const { transactions, fetchMore, canFetchMore, hasMore, networkStatus } =
    useListTransactions(variables)

  const {
    categorySummary,
    loading: loadingSummary,
    exportOptions,
  } = useCombinedCategoriesSummary(intervalOrigin, paginationDirection)

  const isLoading =
    loadingSummary ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore

  return (
    <TransactionsDisplayOptionsContext.Provider value={displayOptions}>
      <TransactionListContext.Provider value={transactionFilters}>
        <BrowserFiltersBar />

        <WithTransactionsUpdates>
          <StyledTransactionsTable
            loading={isLoading}
            categorySummary={categorySummary}
            transactions={transactions}
            exportOptions={exportOptions}
            sorting={transactionFilters.displayOptions.sort.toTableSort}
            onSortingChange={onSortingChange}
            hideBalanceColumn={!!filterTerm.get()}
          />
        </WithTransactionsUpdates>

        <Paginator
          fetchMore={fetchMore}
          networkStatus={networkStatus}
          initialPagination={INITIAL_TRANSACTION_LIST_PAGINATION}
          hasMore={hasMore}
        />

        {isFetchingMore && (
          <LoadingMore>
            <LogoLoading size="30px" />
          </LoadingMore>
        )}
        {!canFetchMore && !isFetchingMore && <NoMoreDataFooter baseRoute={baseRoute} />}
      </TransactionListContext.Provider>
    </TransactionsDisplayOptionsContext.Provider>
  )
}

const NoMoreDataFooter: React.FC<Pick<Props, "baseRoute">> = ({ baseRoute }) => {
  const intervalOrigin = useIntervalOrigin()
  if (!baseRoute) return null

  const previousIntervalOrigin = dateTimeHelper.addIntervalToOrigin(intervalOrigin, -1)
  const path = baseRoute.generateFromCurrentPath(
    { ...previousIntervalOrigin },
    "includeAllQueryParams"
  )

  return (
    <NoMoreData>
      <DigitsLinkButton to={path} size="medium" $variant="secondary-dark">
        {`View ${dateTimeHelper.displayNameFromIntervalOrigin(previousIntervalOrigin)}`}
      </DigitsLinkButton>
    </NoMoreData>
  )
}

const hasNewAvsPage: PaginatorHasMore<ListTransactionsQuery> = (newData: ListTransactionsQuery) =>
  newData.listTransactions.length === INITIAL_TRANSACTION_LIST_PAGINATION.limit

function useListTransactions(variables: ListTransactionsQueryVariables, skipRequest?: boolean) {
  const avsResp = useListTransactionsQuery({
    variables,
    notifyOnNetworkStatusChange: true,
    skip: skipRequest,
    context: { avsBatch: true },
  })

  const transactions = avsResp.data?.listTransactions
  const canFetchMore = !avsResp.data || hasNewAvsPage(avsResp.data)

  const hasMore = hasNewAvsPage
  const { error, fetchMore, networkStatus } = avsResp
  return { transactions, error, fetchMore, canFetchMore, hasMore, networkStatus }
}

function useCombinedCategoriesSummary(
  intervalOrigin: IntervalOrigin,
  paginationDirection: DirectionFromOrigin
) {
  const viewKey = useViewVersion()
  const categoryTypes = useAnalysisCategoryTypes()

  const { data, loading } = useSummarizeTransactionsByCategoryByTimeQuery({
    variables: {
      filter: {
        viewKey,
        categoryTypes,
      },
      origin: {
        ...intervalOrigin,
        intervalCount: 12,
      },
      direction: paginationDirection,
    },
    context: { avsBatch: true },
  })

  const transformToSummary = (
    dimension?: SummarizeTransactionsByCategoryByTimeQuery["dimensionalTransactionSummary"]["category"]
  ) =>
    (dimension || [])
      .map((categorySummary) => {
        if (!hasFirstElement(categorySummary.by.time)) return undefined
        return {
          dimension: categorySummary.categoryObject,
          summary: categorySummary.by.time[0].summary,
          history: [],
          topParties: [],
        }
      })
      .filter(defined)

  return React.useMemo(() => {
    const categorySummaries = convertToDimensionSummaryTree(
      transformToSummary(
        data?.dimensionalTransactionSummary.category.filter((d) =>
          categoryTypes.includes(d.categoryObject.type)
        )
      ),
      0
    )

    const root =
      categorySummaries?.find((s) =>
        [CategoryType.Expenses, CategoryType.Income].includes(s.dimension.type)
      ) ?? categorySummaries?.[0]

    const combinedSummaries: HierarchicalDimensionSummaryNode<EntityCategory> | undefined =
      root && categorySummaries
        ? {
            ...root,
            summary: {
              ...root.summary,
              total: {
                ...root.summary.total,
                transactionsCount:
                  categorySummaries.reduce(
                    (sum, summary) => sum + (summary.summary.total.transactionsCount || 0),
                    0
                  ) || 0,
                value: monetaryValueHelper.addValues(
                  ...(categorySummaries.map((summary) => summary.summary.total.value) || [])
                ),
                moneyFlow: moneyFlowHelper.addValues(
                  ...(categorySummaries.map(
                    (summary) =>
                      summary.summary.total.moneyFlow ?? moneyFlowHelper.buildZeroMoneyFlow()
                  ) || [])
                ),
              },
            },
          }
        : undefined

    const exportOptions: ExportOptions = { type: ExportTransactionsType.Analysis, categoryTypes }
    return {
      categorySummary: combinedSummaries,
      loading,
      exportOptions,
    }
  }, [categoryTypes, data?.dimensionalTransactionSummary.category, loading])
}
